import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings2, Copy, ExternalLink, Download, Loader2, QrCode } from "lucide-react";
import { publicFormUrl } from "@/lib/siport";

// SVG QR dari backend -> PNG resolusi tinggi dengan margin putih & keterangan, siap cetak.
async function svgToPng(svgText, caption) {
  const img = new Image();
  const url = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml" }));
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const size = 1000;
  const pad = 80;
  const canvas = document.createElement("canvas");
  canvas.width = size + pad * 2;
  canvas.height = size + pad * 2 + 90;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, pad, pad, size, size);
  ctx.fillStyle = "#0f3b40";
  ctx.font = "bold 44px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(caption, canvas.width / 2, size + pad + 70);
  URL.revokeObjectURL(url);
  return new Promise((res) => canvas.toBlob(res, "image/png"));
}

export default function SiportPengaturan() {
  const [settings, setSettings] = useState(null);
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const [qrSvg, setQrSvg] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const link = publicFormUrl();

  useEffect(() => {
    api.get("/siport/settings").then((r) => {
      setSettings(r.data);
      setTarget(r.data.target_respon_menit ?? "");
    });
    api.get(`/siport/qr?url=${encodeURIComponent(link)}`, { responseType: "text" }).then((r) => {
      setQrSvg(r.data);
      setQrUrl(URL.createObjectURL(new Blob([r.data], { type: "image/svg+xml" })));
    });
  }, [link]);

  const update = async (body, msg) => {
    setSaving(true);
    try {
      const r = await api.put("/siport/settings", body);
      setSettings(r.data);
      setTarget(r.data.target_respon_menit ?? "");
      toast.success(msg);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveTarget = () => {
    if (target === "" || target == null) return update({ hapus_target: true }, "Target respon time dikosongkan");
    const v = Number(target);
    if (!(v > 0 && v <= 180)) return toast.error("Target harus antara 1–180 menit");
    update({ target_respon_menit: v }, "Target respon time disimpan");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link disalin");
    } catch {
      toast.error("Gagal menyalin, salin manual dari kotak link");
    }
  };

  const downloadQr = async () => {
    try {
      const blob = await svgToPng(qrSvg, "Scan untuk Lapor SIPORT");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "QR_Lapor_SIPORT.png";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Gagal membuat gambar QR");
    }
  };

  if (!settings) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="siport-pengaturan-page">
      <PageHeader icon={Settings2} title="Pengaturan Form Publik"
        subtitle="Buka/tutup form laporan SIPORT, atur target respon time TMRC, dan cetak QR Code untuk meja poli." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Status Form Publik</CardTitle>
              <CardDescription>Jika dinonaktifkan, halaman form menampilkan pesan “Form sedang ditutup”.</CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
                <div>
                  <div className="font-semibold text-slate-900">{settings.form_aktif ? "Form aktif" : "Form ditutup"}</div>
                  <div className="text-sm text-slate-500">{settings.form_aktif ? "Petugas poli bisa mengirim laporan." : "Laporan baru tidak bisa dikirim."}</div>
                </div>
                <Switch checked={!!settings.form_aktif} disabled={saving} data-testid="siport-form-toggle"
                  onCheckedChange={(v) => update({ form_aktif: v }, v ? "Form publik dibuka" : "Form publik ditutup")} />
              </label>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Target Respon Time TMRC</CardTitle>
              <CardDescription>Dipakai untuk KPI “% Code Blue ≤ target” dan garis target di dashboard. Kosongkan jika belum ada target.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-end gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Input type="number" min={1} max={180} value={target} onChange={(e) => setTarget(e.target.value)}
                    placeholder="mis. 5" className="pr-16 bg-white" data-testid="siport-target-input" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">menit</span>
                </div>
              </div>
              <Button onClick={saveTarget} disabled={saving} data-testid="siport-target-save">Simpan</Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Link Form Publik</CardTitle>
              <CardDescription>Bagikan ke perawat/petugas poli. Tidak perlu login.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input readOnly value={link} onFocus={(e) => e.target.select()} className="font-mono text-sm bg-slate-50" data-testid="siport-public-link" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={copy}><Copy className="w-4 h-4 mr-2" />Salin link</Button>
                <Button variant="outline" asChild>
                  <a href={link} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Buka form</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200 shadow-sm h-fit">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><QrCode className="w-5 h-5" />QR Code Form</CardTitle>
            <CardDescription>Unduh lalu cetak dan tempel di meja poli.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              {qrUrl ? <img src={qrUrl} alt="QR Code form Lapor SIPORT" className="w-64 h-64" data-testid="siport-qr" />
                : <div className="w-64 h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
            </div>
            <div className="text-xs text-slate-500 font-mono break-all text-center">{link}</div>
            <Button onClick={downloadQr} disabled={!qrSvg} className="w-full" data-testid="siport-qr-download">
              <Download className="w-4 h-4 mr-2" />Unduh QR Code (PNG)
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
